import React, { useEffect, useState, useRef, useMemo } from 'react';
import { toast } from 'sonner';
import api from '../../lib/api';
import {
  Plus, X, Trash2, Save, Loader2, CheckCircle, AlertTriangle, BookOpen,
  Star, RefreshCw, Sparkles, Zap,
} from 'lucide-react';

const POLL_MS = 6000;
const ACTIVE_STATES = ['pending', 'analyzing'];

// Backend uses `is_active`; we read both defensively so older payloads still work.
const isActive = (v) => !!(v && (v.is_active ?? v.active));

export default function CortexPanel() {
  const [versions, setVersions] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [version, setVersion] = useState(null);
  const [showUpload, setShowUpload] = useState(false);
  const [name, setName] = useState('');
  const [content, setContent] = useState('');
  const [busy, setBusy] = useState(false);
  const [activating, setActivating] = useState(false);
  const [reanalyzing, setReanalyzing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmActivate, setConfirmActivate] = useState(false);
  const [err, setErr] = useState(null);
  const pollRef = useRef(null);

  const loadList = (preserveSelection = true) => {
    return api.get('/api/admin/cortex/versions').then(({ data }) => {
      setVersions(data || []);
      if (!preserveSelection || !selectedId) {
        const active = (data || []).find((v) => isActive(v)) || data?.[0];
        if (active) setSelectedId(active.version_id);
      }
    }).catch(() => {});
  };

  const loadOne = (id) => {
    if (!id) return;
    api.get(`/api/admin/cortex/versions/${id}`).then(({ data }) => {
      setVersion(data);
      if (ACTIVE_STATES.includes(data.analysis_status)) startPolling(id);
      else stopPolling();
    }).catch((e) => setErr(e.message));
  };

  const startPolling = (id) => {
    stopPolling();
    pollRef.current = setInterval(() => {
      api.get(`/api/admin/cortex/versions/${id}`).then(({ data }) => {
        setVersion(data);
        if (!ACTIVE_STATES.includes(data.analysis_status)) { stopPolling(); loadList(); }
      }).catch(() => {});
    }, POLL_MS);
  };
  const stopPolling = () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadList(false); return stopPolling; }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (selectedId) loadOne(selectedId); }, [selectedId]);

  const createVersion = async (e) => {
    e.preventDefault();
    if (!name.trim() || !content.trim()) return;
    setBusy(true); setErr(null);
    try {
      const { data } = await api.post('/api/admin/cortex/versions', { name, content, auto_analyze: true });
      const id = data.version_id || data.id;
      setShowUpload(false); setName(''); setContent('');
      await loadList();
      if (id) setSelectedId(id);
      toast.success('Version created — analysis running in background.');
    } catch (e) { setErr(e.message); toast.error(`Create failed: ${e.message}`); }
    finally { setBusy(false); }
  };

  const updateChapter = (idx, patch) => setVersion((v) => ({ ...v, chapters: v.chapters.map((c, i) => i === idx ? { ...c, ...patch } : c) }));
  const updateBullet = (cIdx, bIdx, val) => updateChapter(cIdx, { summary_points: version.chapters[cIdx].summary_points.map((p, i) => i === bIdx ? val : p) });
  const addBullet = (cIdx) => updateChapter(cIdx, { summary_points: [...version.chapters[cIdx].summary_points, 'New summary point'] });
  const removeBullet = (cIdx, bIdx) => updateChapter(cIdx, { summary_points: version.chapters[cIdx].summary_points.filter((_, i) => i !== bIdx) });
  const addChapter = () => setVersion((v) => ({ ...v, chapters: [...(v.chapters || []), { chapter_id: `ch_${Math.random().toString(36).slice(2, 8)}`, title: 'New Chapter', summary_points: [] }] }));
  const removeChapter = (idx) => setVersion((v) => ({ ...v, chapters: v.chapters.filter((_, i) => i !== idx) }));

  const saveChapters = async () => {
    setBusy(true); setErr(null);
    try {
      await api.put(`/api/admin/cortex/versions/${selectedId}`, { name: version.name, chapters: version.chapters, notes: version.notes || '' });
      await loadList();
      toast.success('Saved.');
    } catch (e) { setErr(e.message); toast.error(`Save failed: ${e.message}`); }
    finally { setBusy(false); }
  };

  const activate = async () => {
    if (!selectedId) return;
    setActivating(true); setErr(null);
    try {
      await api.post(`/api/admin/cortex/versions/${selectedId}/activate`);
      // Reload the list AND the current version so the UI reflects new active state
      await loadList();
      loadOne(selectedId);
      toast.success(`"${version?.name || 'Version'}" is now active. EE will use this version for new chats.`);
      setConfirmActivate(false);
    } catch (e) {
      const msg = e.message || 'Unknown error';
      setErr(msg);
      toast.error(`Activation failed: ${msg}`);
    } finally {
      setActivating(false);
    }
  };

  const reanalyze = async () => {
    if (!selectedId) return;
    setReanalyzing(true); setErr(null);
    try {
      await api.post(`/api/admin/cortex/versions/${selectedId}/reanalyze`);
      loadOne(selectedId);
      toast.success('Re-analysis started. This may take 30–90s.');
    } catch (e) {
      setErr(e.message);
      toast.error(`Re-analyze failed: ${e.message}`);
    } finally {
      setReanalyzing(false);
    }
  };

  const remove = async () => {
    if (!selectedId) return;
    if (!window.confirm('Delete this version? This cannot be undone.')) return;
    setDeleting(true); setErr(null);
    try {
      await api.delete(`/api/admin/cortex/versions/${selectedId}`);
      setSelectedId(null); setVersion(null);
      await loadList(false);
      toast.success('Version deleted.');
    } catch (e) {
      setErr(e.message);
      toast.error(`Delete failed: ${e.message}`);
    } finally {
      setDeleting(false);
    }
  };

  const statusBadge = (s, errMsg) => {
    if (s === 'complete') return <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-500/10 border border-green-500/30 text-green-400 rounded text-xs"><CheckCircle size={10} /> Complete</span>;
    if (s === 'failed') return <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-500/10 border border-red-500/30 text-red-400 rounded text-xs" title={errMsg}><AlertTriangle size={10} /> Failed</span>;
    if (s === 'analyzing' || s === 'pending') return <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-500/10 border border-blue-500/30 text-blue-300 rounded text-xs"><Loader2 className="animate-spin" size={10} /> {s}</span>;
    return <span className="text-xs text-ee-muted">{s || '—'}</span>;
  };

  // Globally-active version (from the list — the source of truth)
  const activeVersion = useMemo(() => versions.find((v) => isActive(v)) || null, [versions]);

  // Status of the currently-selected version
  const selectedIsActive = isActive(version);

  // Can we activate the selected version?
  const canActivate = !!version
    && !selectedIsActive
    && version.analysis_status === 'complete'
    && (version.chapters || []).length > 0;

  const activateBlockedReason = (() => {
    if (!version) return null;
    if (selectedIsActive) return 'Already active.';
    if (ACTIVE_STATES.includes(version.analysis_status)) return 'Analysis still running — wait for it to complete.';
    if (version.analysis_status === 'failed') return 'Analysis failed. Re-analyze or fix the version first.';
    if (!version.chapters || version.chapters.length === 0) return 'Version has no chapters yet. Add chapters or re-analyze.';
    return null;
  })();

  return (
    <div className="space-y-4">
      {/* CURRENTLY ACTIVE BANNER — answers "which version is EE trained on?" */}
      <div className="ee-card p-4 flex items-start gap-3 border-gold/30">
        <div className="w-10 h-10 rounded-full bg-gold/15 border border-gold/40 flex items-center justify-center flex-shrink-0">
          <Star size={18} className="text-gold fill-gold" />
        </div>
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-[0.2em] text-gold/80">EE is currently trained on</div>
          {activeVersion ? (
            <>
              <div className="text-white font-serif text-lg leading-tight">{activeVersion.name}</div>
              <div className="text-xs text-ee-muted mt-0.5">
                {(activeVersion.chapters || []).length} chapters
                {activeVersion.char_count ? ` · ${activeVersion.char_count.toLocaleString()} chars` : ''}
                {' · '}
                <span>this version powers every reader chat with EE.</span>
              </div>
            </>
          ) : (
            <>
              <div className="text-yellow-300 font-serif text-lg leading-tight">No active version</div>
              <div className="text-xs text-ee-muted mt-0.5">EE has no book to reference. Upload a version and activate it to train EE.</div>
            </>
          )}
        </div>
      </div>

      <div className="grid lg:grid-cols-12 gap-6">
        <aside className="lg:col-span-4 ee-card p-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-white font-serif text-lg">Versions</h2>
            <button data-testid="cortex-new" onClick={() => setShowUpload(true)} className="inline-flex items-center gap-1 px-3 py-1.5 rounded gold-grad text-black font-semibold text-xs"><Plus size={12} /> New</button>
          </div>
          <div className="space-y-2">
            {versions.length === 0 && <div className="text-sm text-ee-muted">No versions yet.</div>}
            {versions.map((v) => (
              <button
                key={v.version_id}
                onClick={() => setSelectedId(v.version_id)}
                className={`w-full text-left p-3 rounded-md border transition-colors ${
                  selectedId === v.version_id ? 'border-gold bg-gold/10' :
                  v.active ? 'border-gold/40 bg-ee-bg' :
                  'border-ee-border bg-ee-bg hover:border-gold/30'
                }`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <BookOpen size={14} className="text-gold" />
                  <span className="text-white text-sm truncate flex-1">{v.name}</span>
                  {isActive(v) && (
                    <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-green-500/15 border border-green-500/40 text-green-300 text-[9px] uppercase tracking-wider font-semibold">
                      <CheckCircle size={9} /> Active
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {statusBadge(v.analysis_status, v.analysis_error)}
                  <span className="text-[10px] text-ee-muted">{(v.chapters || []).length} ch</span>
                </div>
              </button>
            ))}
          </div>
        </aside>

        <section className="lg:col-span-8">
          {!version ? (
            <div className="ee-card p-10 text-center text-ee-muted">{selectedId ? 'Loading version…' : 'Select a version from the left, or create a new one.'}</div>
          ) : (
            <div className="ee-card p-6">
              <div className="flex items-start justify-between mb-4 gap-3">
                <div className="flex-1 min-w-0">
                  <input value={version.name} onChange={(e) => setVersion({ ...version, name: e.target.value })} className="text-2xl font-serif text-white bg-transparent outline-none w-full" />
                  <div className="flex items-center flex-wrap gap-3 mt-2 text-xs text-ee-muted">
                    {statusBadge(version.analysis_status, version.analysis_error)}
                    <span>{version.char_count?.toLocaleString() || 0} chars</span>
                    <span>{(version.chapters || []).length} chapters</span>
                    {selectedIsActive && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-green-500/15 border border-green-500/40 text-green-300 text-[10px] font-semibold uppercase tracking-wider">
                        <CheckCircle size={10} /> Currently Active
                      </span>
                    )}
                  </div>
                  {version.analysis_status === 'failed' && version.analysis_error && (
                    <div className="mt-2 text-xs text-red-400 bg-red-400/10 border border-red-400/30 rounded p-2">{version.analysis_error}</div>
                  )}
                  {ACTIVE_STATES.includes(version.analysis_status) && (
                    <div className="mt-2 text-xs text-blue-300 bg-blue-500/10 border border-blue-500/30 rounded p-2 inline-flex items-center gap-2">
                      <Loader2 className="animate-spin" size={12} /> Analyzing book in background. This may take 30–90 seconds for a full manuscript. Auto-refreshing every {POLL_MS/1000}s.
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {selectedIsActive ? (
                    <button
                      data-testid="cortex-activated-state"
                      disabled
                      title="EE is currently using this version"
                      className="px-3 py-1.5 rounded text-xs inline-flex items-center gap-1 bg-green-500/15 border border-green-500/40 text-green-300 font-semibold cursor-default"
                    >
                      <CheckCircle size={12} /> Activated
                    </button>
                  ) : (
                    <button
                      data-testid="cortex-activate"
                      onClick={() => setConfirmActivate(true)}
                      disabled={!canActivate || activating}
                      title={activateBlockedReason || 'Make this the version EE chats from'}
                      className={`px-3 py-1.5 rounded text-xs inline-flex items-center gap-1 transition-colors ${
                        canActivate
                          ? 'gold-grad text-black font-semibold hover:opacity-90'
                          : 'border border-ee-border text-ee-muted cursor-not-allowed'
                      } disabled:opacity-60`}
                    >
                      {activating ? <Loader2 className="animate-spin" size={12} /> : <Zap size={12} />}
                      {activating ? 'Activating…' : 'Activate for EE'}
                    </button>
                  )}
                  <button
                    onClick={reanalyze}
                    disabled={reanalyzing || ACTIVE_STATES.includes(version.analysis_status)}
                    className="px-3 py-1.5 rounded border border-ee-border text-ee-text2 text-xs hover:border-gold/40 inline-flex items-center gap-1 disabled:opacity-50"
                  >
                    {reanalyzing ? <Loader2 className="animate-spin" size={12} /> : <RefreshCw size={12} />}
                    {reanalyzing ? 'Re-analyzing…' : 'Re-analyze'}
                  </button>
                  <button
                    onClick={remove}
                    disabled={deleting || selectedIsActive}
                    title={selectedIsActive ? "Can't delete the active version" : 'Delete this version'}
                    className="p-1.5 text-red-400 hover:bg-red-400/10 rounded disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {deleting ? <Loader2 className="animate-spin" size={14} /> : <Trash2 size={14} />}
                  </button>
                </div>
              </div>

              {!selectedIsActive && activateBlockedReason && !ACTIVE_STATES.includes(version.analysis_status) && (
                <div className="mb-4 text-xs text-yellow-300 bg-yellow-500/10 border border-yellow-500/30 rounded p-2 inline-flex items-center gap-2">
                  <AlertTriangle size={12} /> Can't activate yet: {activateBlockedReason}
                </div>
              )}

              <div className="space-y-3 max-h-[55vh] overflow-y-auto pr-1">
                {(version.chapters || []).map((c, ci) => (
                  <div key={c.chapter_id || ci} className="bg-ee-bg border border-ee-border rounded-md p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xs text-gold/80">{String(ci + 1).padStart(2, '0')}</span>
                      <input value={c.title} onChange={(e) => updateChapter(ci, { title: e.target.value })} className="flex-1 bg-transparent text-white font-serif text-lg outline-none" />
                      <button onClick={() => removeChapter(ci)} className="text-red-400 hover:text-red-300"><Trash2 size={14} /></button>
                    </div>
                    <ul className="space-y-1 ml-4">
                      {(c.summary_points || []).map((p, bi) => (
                        <li key={bi} className="flex items-start gap-2 group">
                          <span className="mt-2 w-1 h-1 rounded-full bg-gold flex-shrink-0" />
                          <textarea value={p} onChange={(e) => updateBullet(ci, bi, e.target.value)} rows={Math.max(1, p.split('\n').length)} className="flex-1 bg-transparent text-ee-text2 text-sm outline-none resize-none" />
                          <button onClick={() => removeBullet(ci, bi)} className="text-ee-muted opacity-0 group-hover:opacity-100 hover:text-red-400"><X size={12} /></button>
                        </li>
                      ))}
                    </ul>
                    <button onClick={() => addBullet(ci)} className="mt-2 ml-4 text-xs text-gold hover:underline">+ Add bullet</button>
                  </div>
                ))}
                <button onClick={addChapter} className="w-full py-2 rounded border border-dashed border-gold/40 text-gold text-sm hover:bg-gold/5 inline-flex items-center justify-center gap-1"><Plus size={14} /> Add Chapter</button>
              </div>

              {err && <div className="mt-3 text-xs text-red-400">{err}</div>}
              <div className="mt-4 flex items-center justify-end gap-2">
                <button data-testid="save-cortex" onClick={saveChapters} disabled={busy} className="inline-flex items-center gap-1 px-4 py-2 rounded gold-grad text-black font-semibold text-sm disabled:opacity-50"><Save size={14} /> {busy ? 'Saving…' : 'Save & Re-train'}</button>
              </div>
            </div>
          )}
        </section>
      </div>

      {/* Upload modal */}
      {showUpload && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" onClick={() => !busy && setShowUpload(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={createVersion} className="ee-card w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2"><Sparkles size={18} className="text-gold" /><h3 className="text-white font-serif text-xl">New Book Version</h3></div>
              <button type="button" onClick={() => setShowUpload(false)} disabled={busy} className="text-ee-muted hover:text-white"><X size={18} /></button>
            </div>
            <p className="text-xs text-ee-muted mb-4">Paste the full book content. AI will extract chapters &amp; summaries automatically (30–90s for a full manuscript). The new version starts as a draft — activate it when you're happy with the analysis.</p>
            <label className="block mb-3"><span className="text-xs uppercase tracking-wider text-gold/80">Version Name</span>
              <input data-testid="cortex-name" required value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. v1.0 — Final Draft" className="mt-1 w-full bg-ee-bg border border-ee-border rounded-md p-2 text-white" />
            </label>
            <label className="block"><span className="text-xs uppercase tracking-wider text-gold/80">Content</span>
              <textarea data-testid="cortex-content" required value={content} onChange={(e) => setContent(e.target.value)} rows={14} placeholder="Paste the full book manuscript here…" className="mt-1 w-full bg-ee-bg border border-ee-border rounded-md p-2 text-sm text-white font-mono" />
              <span className="text-xs text-ee-muted mt-1 block">{content.length.toLocaleString()} characters</span>
            </label>
            {err && <div className="mt-3 text-sm text-red-400 bg-red-400/10 border border-red-400/30 rounded-md p-2">{err}</div>}
            <div className="mt-4 flex justify-end gap-2">
              <button type="button" onClick={() => setShowUpload(false)} disabled={busy} className="px-4 py-2 rounded border border-ee-border text-ee-text2 text-sm">Cancel</button>
              <button data-testid="cortex-create" type="submit" disabled={busy} className="inline-flex items-center gap-1 px-4 py-2 rounded gold-grad text-black font-semibold text-sm disabled:opacity-50">{busy ? 'Creating…' : 'Create & Analyze'}</button>
            </div>
          </form>
        </div>
      )}

      {/* Activate confirmation modal */}
      {confirmActivate && version && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" onClick={() => !activating && setConfirmActivate(false)}>
          <div className="ee-card w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-gold/15 border border-gold/40 flex items-center justify-center flex-shrink-0">
                <Star size={18} className="text-gold fill-gold" />
              </div>
              <div>
                <h3 className="text-white font-serif text-lg leading-tight">Activate this version for EE?</h3>
                <p className="text-ee-muted text-sm mt-1">
                  <span className="text-white">"{version.name}"</span> will become the version EE references for every reader chat.
                  {activeVersion && activeVersion.version_id !== version.version_id && (
                    <> The current active version <span className="text-white">"{activeVersion.name}"</span> will be deactivated.</>
                  )}
                </p>
              </div>
            </div>
            {err && <div className="mb-3 text-xs text-red-400 bg-red-400/10 border border-red-400/30 rounded p-2">{err}</div>}
            <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end gap-2 mt-5">
              <button onClick={() => setConfirmActivate(false)} disabled={activating} className="px-3 py-2 rounded border border-ee-border text-ee-text2 text-sm hover:text-white disabled:opacity-50">Cancel</button>
              <button
                data-testid="cortex-activate-confirm"
                onClick={activate}
                disabled={activating}
                className="px-4 py-2 rounded gold-grad text-black font-semibold text-sm inline-flex items-center gap-1 disabled:opacity-60"
              >
                {activating ? <Loader2 className="animate-spin" size={14} /> : <Zap size={14} />}
                {activating ? 'Activating…' : 'Activate for EE'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
